import { useState } from 'preact/hooks';
import { Modal } from 'components/ModalV2';
import type Shipment from 'interfaces/PmParcel';
import { isEmptyObject } from 'utils/object.utils';

import { useTranslation } from 'react-i18next';
import { Fragment } from 'preact/jsx-runtime';
import { EditMode } from '../EditMode';
import { ViewMode } from '../ViewMode';
import { ModalFooter } from '../ModalFooter';
import { useSteps } from '../../hooks/useSteps.hook';
import { useReturnData } from '../../hooks/useReturnData.hook';
import type { ReturnRequestError } from '../../interfaces/ReturnRequest.interface';
import { STEPS } from '../../constants/ReturnRequest.constant';
import { ContentContainer } from './ReturnRequestModal.style';
import { useConfirmCTA } from './hooks/useConfirmCTA.hook';

const FORM_TITLE_ID = 'return-request-form-title';

interface ReturnRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  shipment: Shipment;
}

const ReturnRequestModal = ({
  isOpen,
  onClose,
  shipment,
}: ReturnRequestModalProps) => {
  const { t } = useTranslation();
  const { currentStep, goToEditStep, goToViewStep } = useSteps();

  const [errors, setError] = useState<ReturnRequestError>({});
  const [apiError, setApiError] = useState<string>();

  const {
    onCheckLineItems,
    onUncheckLineItems,
    onSelectItemQuantity,
    onSelectReasonForReturn,
    onChangeCustomReasonForReturn,
    onUpdateComment,
    onValidateReturnData,
    returnRequestFormData,
    attachmentRef, // ref pass to Attachment component
    trimOtherReasons,
  } = useReturnData();

  const handleNextButton = () => {
    const errors = onValidateReturnData();

    if (isEmptyObject(errors)) {
      setError({});
      trimOtherReasons();
      goToViewStep();
    } else {
      setError(errors);
    }
    setApiError(undefined);
  };

  const { handleConfirmButton, isConfirming } = useConfirmCTA(
    shipment,
    returnRequestFormData,
    setApiError,
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closable
      aria={{
        labelledby: FORM_TITLE_ID,
      }}
    >
      <Modal.Header id={FORM_TITLE_ID} key={currentStep}>
        {currentStep === STEPS.EDIT
          ? t('RETURN_WHAT_ITEMS_TO_RETURN')
          : t('RETURN_CONFIRM_RETURN_ITEMS')}
      </Modal.Header>
      <Modal.Body>
        <Fragment>
          <ContentContainer>
            {currentStep === STEPS.EDIT ? (
              <EditMode
                shipment={shipment}
                onCheckLineItems={onCheckLineItems}
                onUncheckLineItems={onUncheckLineItems}
                onSelectItemQuantity={onSelectItemQuantity}
                onSelectReasonForReturn={onSelectReasonForReturn}
                onChangeCustomReasonForReturn={onChangeCustomReasonForReturn}
                onUpdateComment={onUpdateComment}
                returnData={returnRequestFormData}
                errors={errors}
                attachmentRef={attachmentRef}
                formTitleId={FORM_TITLE_ID}
              />
            ) : (
              <ViewMode
                returnData={returnRequestFormData}
                error={apiError}
                formTitleId={FORM_TITLE_ID}
              />
            )}
          </ContentContainer>
          <ModalFooter
            onCloseModal={onClose}
            goToEditMode={goToEditStep}
            currentStep={currentStep}
            handleNextButton={handleNextButton}
            handleConfirmButton={handleConfirmButton}
            isConfirming={isConfirming}
            error={errors.error}
          />
        </Fragment>
      </Modal.Body>
    </Modal>
  );
};

export { ReturnRequestModal };
